import {
    SCHEME_DARK,
    SCHEME_DARK_HIGH_CONTRAST,
    SCHEME_LIGHT,
    SCHEME_LIGHT_HIGH_CONTRAST,
    isHighContrastScheme,
} from '../appearance/appearanceSchemes';

export const BACKOFFICE_ACTION_COLOR = '#0066CC';

const BODY_FONT_FAMILY = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

const grey = {
    50: '#F5F5F5',
    100: '#E8E8E8',
    200: '#D1D1D1',
    300: '#A8A8A8',
    400: '#8D8D8D',
    500: '#6F6F6F',
    600: '#525252',
    700: '#393939',
    800: '#262626',
    900: '#161616',
};

const text = {
    primary: grey[900],
    secondary: grey[600],
    disabled: grey[300],
};

const selectionTints = {
    hover: 'rgba(51, 153, 255, 0.08)',
    selected: 'rgba(51, 153, 255, 0.12)',
};

const fontWeights = {
    regular: 400,
    medium: 500,
    semibold: 600,
    black: 900,
};

const typeScale = {
    displayS: {
        fontWeight: fontWeights.black,
        fontSize: '2.25rem',
        lineHeight: 1.3,
        letterSpacing: '-0.02em',
    },
    headlineL: {
        fontWeight: fontWeights.black,
        fontSize: '1.875rem',
        lineHeight: 1.4,
        letterSpacing: '-0.01em',
    },
    headlineM: {
        fontWeight: fontWeights.black,
        fontSize: '1.5rem',
        lineHeight: 1.4,
        letterSpacing: '-0.01em',
    },
    headlineS: {
        fontWeight: fontWeights.black,
        fontSize: '1.25rem',
        lineHeight: 1.5,
        letterSpacing: '0em',
    },
    titleL: {
        fontWeight: fontWeights.semibold,
        fontSize: '1.125rem',
        lineHeight: 1.44,
        letterSpacing: '0em',
    },
    titleM: {
        fontWeight: fontWeights.semibold,
        fontSize: '1rem',
        lineHeight: 1.5,
        letterSpacing: '0em',
    },
    titleS: {
        fontWeight: fontWeights.semibold,
        fontSize: '0.875rem',
        lineHeight: 1.43,
        letterSpacing: '0.01em',
    },
    bodyM: {
        fontWeight: fontWeights.regular,
        fontSize: '1rem',
        lineHeight: 1.5,
        letterSpacing: '0em',
    },
    bodyS: {
        fontWeight: fontWeights.regular,
        fontSize: '0.875rem',
        lineHeight: 1.43,
        letterSpacing: '0.01em',
    },
    labelL: {
        fontWeight: fontWeights.medium,
        fontSize: '0.875rem',
        lineHeight: 1.43,
        letterSpacing: '0.01em',
    },
    labelM: {
        fontWeight: fontWeights.medium,
        fontSize: '0.75rem',
        lineHeight: 1.33,
        letterSpacing: '0.02em',
    },
    labelS: {
        fontWeight: fontWeights.medium,
        fontSize: '0.6875rem',
        lineHeight: 1.27,
        letterSpacing: '0.02em',
    },
};

const shareBrand = {
    whatsapp: '#25D366',
    telegram: '#0088CC',
    facebook: '#1877F2',
    messenger: '#0084FF',
    instagram: '#E4405F',
    viber: '#7360F2',
    linkedin: '#0A66C2',
    email: '#6B7280',
    link: '#3399FF',
};

const statusText = {
    success: '#1A6B36',
    warning: '#854D0E',
};

export const v20 = {
    palette: {
        primary: {
            main: '#3399FF',
            light: '#EEEEFF',
            dark: '#000099',
            contrastText: '#FFFFFF',
            text: BACKOFFICE_ACTION_COLOR,
        },
        error: {
            main: '#DA1E28',
            light: '#FF8389',
            dark: '#A2191F',
            contrastText: '#FFFFFF',
        },
        success: {
            main: '#24A148',
            light: '#42BE65',
            dark: '#198038',
            contrastText: '#FFFFFF',
        },
        warning: {
            main: '#F1C21B',
            light: '#F8D765',
            dark: '#D4A90A',
            contrastText: grey[900],
        },
        manageEvents: {
            main: '#33CC66',
            dark: '#1F7A3D',
            light: 'rgba(51, 204, 102, 0.1)',
            contrastText: '#FFFFFF',
        },
        ticketNote: {
            limitedBackground: '#FEF2F2',
            limitedText: '#DC2626',
            standardBackground: '#FEFCE8',
            standardText: statusText.warning,
        },
        divider: grey[100],
        selection: {
            bg: selectionTints.hover,
            bgStrong: selectionTints.selected,
        },
        badge: {
            sellingFast: { bg: '#FFF4E5', fg: '#C2410C' },
            popular: { bg: '#EAF8EF', fg: statusText.success },
            soldOut: { bg: '#FEF2F2', fg: '#DC2626' },
            neutral: { bg: selectionTints.selected, fg: '#3399FF' },
        },
        categoryBadge: {
            default: { bg: '#F3F4F6', fg: '#1F2937' },
            error: { bg: '#FDF2F2', fg: '#93192E' },
        },
        imagePlaceholder: '#F3F4F6',
        shareBrand,
        brandSurface: {
            main: '#000099',
            contrastText: '#FFFFFF',
            error: '#FF8389',
            success: '#42BE65',
            actionHover: '#EEEEFF',
            focus: '#3399FF',
        },
    },
    grey,
    text,
    background: {
        default: '#FFFFFF',
        paper: '#FFFFFF',
    },
    fonts: {
        body: BODY_FONT_FAMILY,
        heading: `"Mulish", ${BODY_FONT_FAMILY}`,
    },
    fontWeights,
    typeScale,
    radii: {
        small: 6,
        default: 8,
        large: 12,
    },
    spacing: 8,
    headerHeights: {
        xs: 56,
        md: 64,
    },
    maxWidth: 1180,
};

const WHITE = '#FFFFFF';
const BLACK = '#000000';
const YELLOW = '#FFFF00';

const paleGrey = {
    10: '#F4F4F4',
    20: '#E0E0E0',
    30: '#C6C6C6',
};

const darkGrey = {
    50: '#1F1F1F',
    100: grey[800],
    200: grey[700],
    300: grey[600],
    400: grey[400],
    500: grey[300],
    600: '#B8B8B8',
    700: paleGrey[30],
    800: paleGrey[20],
    900: paleGrey[10],
};

const lightHighContrastGrey = {
    50: paleGrey[10],
    100: paleGrey[20],
    200: grey[400],
    300: grey[500],
    400: grey[600],
    500: grey[700],
    600: grey[800],
    700: grey[900],
    800: BLACK,
    900: BLACK,
};

const darkHighContrastGrey = {
    50: '#1A1A1A',
    100: grey[800],
    200: grey[400],
    300: grey[300],
    400: paleGrey[30],
    500: paleGrey[20],
    600: paleGrey[10],
    700: WHITE,
    800: WHITE,
    900: WHITE,
};

export const schemeHeaderColors = {
    [SCHEME_LIGHT]: WHITE,
    [SCHEME_DARK]: grey[900],
    [SCHEME_LIGHT_HIGH_CONTRAST]: WHITE,
    [SCHEME_DARK_HIGH_CONTRAST]: BLACK,
};

function createMonochromeStatusColors(ink, page) {
    return {
        error: ink,
        errorTint: page,
        errorStrong: ink,
        success: ink,
        successTint: page,
        successStrong: ink,
        warning: ink,
        warningTint: page,
        warningStrong: ink,
        onStatus: page,
        onWarning: page,
    };
}

const schemeStatusColors = {
    [SCHEME_DARK]: {
        action: '#3399FF',
        actionTint: '#12253A',
        actionStrong: '#99CCFF',
        onAction: grey[900],
        error: '#FF8389',
        errorTint: '#3A1A1C',
        errorStrong: '#FFB3B8',
        success: '#42BE65',
        successTint: '#0E3320',
        successStrong: '#6FDC8C',
        warning: '#F1C21B',
        warningTint: '#332B12',
        warningStrong: '#F8D765',
        onStatus: grey[900],
        onWarning: grey[900],
    },
    [SCHEME_LIGHT_HIGH_CONTRAST]: {
        action: BLACK,
        actionTint: paleGrey[20],
        actionStrong: BLACK,
        onAction: WHITE,
        ...createMonochromeStatusColors(BLACK, WHITE),
    },
    [SCHEME_DARK_HIGH_CONTRAST]: {
        action: YELLOW,
        actionTint: grey[800],
        actionStrong: YELLOW,
        onAction: BLACK,
        ...createMonochromeStatusColors(WHITE, BLACK),
    },
};

const schemeNeutrals = {
    [SCHEME_DARK]: {
        grey: darkGrey,
        text: {
            primary: darkGrey[900],
            secondary: darkGrey[700],
            disabled: darkGrey[400],
        },
        divider: darkGrey[200],
        inputBorder: darkGrey[500],
        page: schemeHeaderColors[SCHEME_DARK],
        surface: darkGrey[100],
        selectionHover: 'rgba(51, 153, 255, 0.16)',
        selectionSelected: 'rgba(51, 153, 255, 0.24)',
        sellingFastTint: '#3D2410',
        sellingFastText: '#FFB784',
        brandSurface: '#0D1B4D',
        onBrandSurface: paleGrey[10],
        errorOnBrandSurface: '#FF8389',
        successOnBrandSurface: '#42BE65',
        brandSurfaceActionHover: '#DCE3F7',
        brandSurfaceFocus: '#3399FF',
    },
    [SCHEME_LIGHT_HIGH_CONTRAST]: {
        grey: lightHighContrastGrey,
        text: {
            primary: BLACK,
            secondary: grey[800],
            disabled: grey[600],
        },
        divider: grey[500],
        inputBorder: grey[900],
        page: schemeHeaderColors[SCHEME_LIGHT_HIGH_CONTRAST],
        surface: WHITE,
        selectionHover: 'rgba(255, 255, 0, 0.35)',
        selectionSelected: YELLOW,
        brandSurface: BLACK,
        onBrandSurface: WHITE,
        errorOnBrandSurface: WHITE,
        successOnBrandSurface: WHITE,
        brandSurfaceActionHover: YELLOW,
        brandSurfaceFocus: YELLOW,
    },
    [SCHEME_DARK_HIGH_CONTRAST]: {
        grey: darkHighContrastGrey,
        text: {
            primary: WHITE,
            secondary: paleGrey[20],
            disabled: grey[300],
        },
        divider: grey[400],
        inputBorder: paleGrey[20],
        page: schemeHeaderColors[SCHEME_DARK_HIGH_CONTRAST],
        surface: BLACK,
        selectionHover: 'rgba(255, 255, 0, 0.18)',
        selectionSelected: 'rgba(255, 255, 0, 0.32)',
        brandSurface: BLACK,
        onBrandSurface: WHITE,
        errorOnBrandSurface: WHITE,
        successOnBrandSurface: WHITE,
        brandSurfaceActionHover: YELLOW,
        brandSurfaceFocus: YELLOW,
    },
};

function createPromotionBadges(scheme, status, neutrals) {
    if (isHighContrastScheme(scheme)) {
        const plainBadge = { bg: neutrals.page, fg: neutrals.text.primary };

        return { sellingFast: plainBadge, popular: plainBadge };
    }

    return {
        sellingFast: { bg: neutrals.sellingFastTint, fg: neutrals.sellingFastText },
        popular: { bg: status.successTint, fg: status.successStrong },
    };
}

function createShareBrandColors(scheme, neutrals) {
    if (!isHighContrastScheme(scheme)) {
        return shareBrand;
    }

    return Object.keys(shareBrand).reduce(
        (colors, platform) => ({ ...colors, [platform]: neutrals.text.primary }),
        {},
    );
}

function createPublicScheme(scheme) {
    const status = schemeStatusColors[scheme];
    const neutrals = schemeNeutrals[scheme];

    return {
        palette: {
            primary: {
                main: status.action,
                light: status.actionTint,
                dark: status.actionStrong,
                contrastText: status.onAction,
                text: status.action,
            },
            error: {
                main: status.error,
                light: status.errorTint,
                dark: status.errorStrong,
                contrastText: status.onStatus,
            },
            success: {
                main: status.success,
                light: status.successTint,
                dark: status.successStrong,
                contrastText: status.onStatus,
            },
            warning: {
                main: status.warning,
                light: status.warningTint,
                dark: status.warningStrong,
                contrastText: status.onWarning,
            },
            manageEvents: {
                main: status.success,
                dark: status.successStrong,
                light: status.successTint,
                contrastText: status.onStatus,
            },
            ticketNote: {
                limitedBackground: status.errorTint,
                limitedText: status.errorStrong,
                standardBackground: status.warningTint,
                standardText: status.warningStrong,
            },
            divider: neutrals.divider,
            selection: {
                bg: neutrals.selectionHover,
                bgStrong: neutrals.selectionSelected,
            },
            badge: {
                ...createPromotionBadges(scheme, status, neutrals),
                soldOut: { bg: status.errorTint, fg: status.errorStrong },
                neutral: { bg: status.actionTint, fg: status.actionStrong },
            },
            categoryBadge: {
                default: { bg: neutrals.grey[50], fg: neutrals.text.primary },
                error: { bg: status.errorTint, fg: status.errorStrong },
            },
            imagePlaceholder: neutrals.grey[100],
            shareBrand: createShareBrandColors(scheme, neutrals),
            brandSurface: {
                main: neutrals.brandSurface,
                contrastText: neutrals.onBrandSurface,
                error: neutrals.errorOnBrandSurface,
                success: neutrals.successOnBrandSurface,
                actionHover: neutrals.brandSurfaceActionHover,
                focus: neutrals.brandSurfaceFocus,
            },
        },
        grey: neutrals.grey,
        text: neutrals.text,
        background: {
            default: neutrals.page,
            paper: neutrals.page,
        },
    };
}

export const v20Schemes = {
    [SCHEME_LIGHT]: {
        palette: v20.palette,
        grey: v20.grey,
        text: v20.text,
        background: v20.background,
    },
    [SCHEME_DARK]: createPublicScheme(SCHEME_DARK),
    [SCHEME_LIGHT_HIGH_CONTRAST]: createPublicScheme(SCHEME_LIGHT_HIGH_CONTRAST),
    [SCHEME_DARK_HIGH_CONTRAST]: createPublicScheme(SCHEME_DARK_HIGH_CONTRAST),
};

const CHART_LINE_ON_DARK = '#CCE5FF';
const CHART_LINE_BRIGHT = '#1D6BFF';
const CHART_LINE_ON_LIGHT = '#1192E8';

const LIGHT_CHART_CATEGORICAL = [
    '#6929C4',
    CHART_LINE_ON_LIGHT,
    '#005D5D',
    '#9F1853',
    '#FA4D56',
    '#570408',
    '#198038',
    '#002D9C',
    '#EE538B',
    '#B28600',
];

const DARK_CHART_CATEGORICAL = [
    '#8A3FFC',
    '#33B1FF',
    '#007D79',
    '#FF7EB6',
    '#FA4D56',
    '#FFF1F1',
    '#6FDC8C',
    '#4589FF',
    '#D12771',
    '#D2A106',
];

function createChartColors(first, second, categorical = [first, second]) {
    return { first, second, categorical };
}

const schemeChartColors = {
    [SCHEME_DARK]: createChartColors(CHART_LINE_BRIGHT, CHART_LINE_ON_DARK, DARK_CHART_CATEGORICAL),
    [SCHEME_LIGHT_HIGH_CONTRAST]: createChartColors(BLACK, grey[500]),
    [SCHEME_DARK_HIGH_CONTRAST]: createChartColors(WHITE, grey[400]),
};

const schemePlaceholderColors = {
    [SCHEME_DARK]: grey[700],
    [SCHEME_LIGHT_HIGH_CONTRAST]: grey[200],
    [SCHEME_DARK_HIGH_CONTRAST]: grey[700],
};

function createBackofficePalette(scheme) {
    const status = schemeStatusColors[scheme];
    const neutrals = schemeNeutrals[scheme];
    const chart = schemeChartColors[scheme];

    return {
        action: status.action,
        actionContrastText: status.onAction,
        actionTint: status.actionTint,
        actionStrong: status.actionStrong,
        chartFirst: chart.first,
        chartSecond: chart.second,
        chartCategorical: chart.categorical,
        background: neutrals.page,
        surface: neutrals.surface,
        header: schemeHeaderColors[scheme],
        textPrimary: neutrals.text.primary,
        textSecondary: neutrals.text.secondary,
        textDisabled: neutrals.text.disabled,
        textStrong: neutrals.grey[800],
        divider: neutrals.divider,
        inputBorder: neutrals.inputBorder,
        placeholder: schemePlaceholderColors[scheme],
        selectionHover: neutrals.selectionHover,
        selectionSelected: neutrals.selectionSelected,
        error: status.error,
        errorLight: status.errorTint,
        errorDark: status.errorStrong,
        errorContrastText: status.onStatus,
        errorBackground: status.errorTint,
        success: status.success,
        successLight: status.successTint,
        successDark: status.successStrong,
        successContrastText: status.onStatus,
        successBackground: status.successTint,
        warning: status.warning,
        warningLight: status.warningTint,
        warningDark: status.warningStrong,
        warningContrastText: status.onWarning,
    };
}

export const backofficePalettes = {
    [SCHEME_LIGHT]: {
        action: BACKOFFICE_ACTION_COLOR,
        actionContrastText: WHITE,
        actionTint: v20.palette.primary.light,
        actionStrong: v20.palette.primary.dark,
        chartFirst: CHART_LINE_ON_LIGHT,
        chartSecond: v20.palette.primary.dark,
        chartCategorical: LIGHT_CHART_CATEGORICAL,
        background: grey[50],
        surface: WHITE,
        header: schemeHeaderColors[SCHEME_LIGHT],
        textPrimary: text.primary,
        textSecondary: text.secondary,
        textDisabled: text.disabled,
        textStrong: grey[700],
        divider: grey[100],
        inputBorder: grey[400],
        placeholder: grey[100],
        selectionHover: selectionTints.hover,
        selectionSelected: selectionTints.selected,
        error: '#DA1E28',
        errorLight: v20.palette.error.light,
        errorDark: v20.palette.error.dark,
        errorContrastText: v20.palette.error.contrastText,
        errorBackground: '#FEF2F2',
        success: '#198038',
        successLight: '#24A148',
        successDark: statusText.success,
        successContrastText: v20.palette.success.contrastText,
        successBackground: '#EAF8EF',
        warning: '#F1C21B',
        warningLight: v20.palette.warning.light,
        warningDark: statusText.warning,
        warningContrastText: grey[900],
    },
    [SCHEME_DARK]: createBackofficePalette(SCHEME_DARK),
    [SCHEME_LIGHT_HIGH_CONTRAST]: createBackofficePalette(SCHEME_LIGHT_HIGH_CONTRAST),
    [SCHEME_DARK_HIGH_CONTRAST]: createBackofficePalette(SCHEME_DARK_HIGH_CONTRAST),
};

export const backofficeNeutrals = {
    [SCHEME_DARK]: {
        50: darkGrey[50],
        100: '#2C2C2C',
        200: darkGrey[200],
        300: grey[500],
        400: darkGrey[400],
        500: darkGrey[500],
        600: darkGrey[600],
        700: darkGrey[700],
        800: darkGrey[800],
        900: darkGrey[900],
    },
    [SCHEME_LIGHT_HIGH_CONTRAST]: {
        50: paleGrey[10],
        100: '#EDEDED',
        200: paleGrey[20],
        300: grey[500],
        400: grey[600],
        500: grey[700],
        600: grey[800],
        700: grey[900],
        800: BLACK,
        900: BLACK,
    },
    [SCHEME_DARK_HIGH_CONTRAST]: {
        50: '#0A0A0A',
        100: '#141414',
        200: darkHighContrastGrey[50],
        300: grey[300],
        400: paleGrey[30],
        500: paleGrey[20],
        600: paleGrey[10],
        700: WHITE,
        800: WHITE,
        900: WHITE,
    },
};
